import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import { ITEM_TYPE_INFO_MAP } from 'Angular/features/components/saved-items/open-item-modal/constants';
import { SavedItemType } from 'Angular/features/components/saved-items/enums/saved-item-type.enum';
import { OpenItemModalAction } from 'Angular/features/components/saved-items/open-item-modal/enums/open-item-modal-action.enum';
import { FormattedShareableItem } from 'Angular/features/components/saved-items/open-item-modal/interfaces/serialized-shareable-item.interface';
import { ShareableItemTypeInfo } from 'Angular/features/components/saved-items/open-item-modal/interfaces/shareable-item-type-info-map.interface';
import { ShareableItem } from 'SharingApp/ts/interfaces/shareable-item.interface';

import { OpenItemListComponent } from 'Angular/features/components/saved-items/open-item-modal/components/open-item-list/open-item-list.component';

/**
 * Open Item Tabs Component
 *
 * Display two tabs for the owned and shared saved items.
 *
 * Inputs:
 * ownItems: List with the items owned by the user
 * sharedItems: List with the items shared by other users
 * selectOnly: whether items in the list should be able to be edited/deleted or only selected.
 * savedItemType: item type according to SavedItemType
 * sharedWithMeEnabled: whether the Shared tab should be enabled or not
 * itemTypeInfo: Saved item type metadata
 * showRadioButtons: displays a radio button for each item in the list.
 *
 * Outputs:
 * doActionOnItemHandler: Action to be performed when one of the items from any tab is selected.
 */
@Component({
    selector: 'ycn-open-item-tabs',
    templateUrl: './open-item-tabs.template.html',
    standalone: false,
})
export class OpenItemTabsComponent implements OnInit, OnChanges {
    @ViewChild('ownItemListComponent') ownItemListComponent!: OpenItemListComponent;

    @ViewChild('sharedItemListComponent') sharedItemListComponent!: OpenItemListComponent;

    @Input() ownItems: ShareableItem[] = [];

    @Input() selectOnly: boolean = false;

    @Input() savedItemType!: SavedItemType;

    @Input() sharedItems: ShareableItem[] = [];

    @Input() sharedWithMeEnabled: boolean = true;

    @Input() showRadioButtons: boolean = false;

    @Output() doActionOnItemHandler = new EventEmitter<{
        item: FormattedShareableItem | null;
        actionType: OpenItemModalAction;
    }>();

    itemTypeInfo: ShareableItemTypeInfo | null = null;

    myItemsLabel: string = '';

    ownSelectedItemId: number | null = null;

    sharedSelectedItemId: number | null = null;

    activeTab: 'shared' | 'own' = 'own';

    ngOnInit(): void {
        this.setActiveTab('own');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.savedItemType && changes.savedItemType.currentValue) {
            const savedItemType: SavedItemType = changes.savedItemType.currentValue;
            const itemTypeInfo = ITEM_TYPE_INFO_MAP[savedItemType];
            if (itemTypeInfo != null) {
                this.myItemsLabel = `My ${itemTypeInfo.pluralLabel}`;
                this.itemTypeInfo = itemTypeInfo;
            }
        }
    }

    doActionOnItem(itemAndAction: { item: FormattedShareableItem | null; actionType: OpenItemModalAction }): void {
        this.doActionOnItemHandler.emit(itemAndAction);
    }

    setActiveTab(tab: 'shared' | 'own'): void {
        // Set the active tab to shared if a user does not have any of their own items
        this.activeTab = !this.ownItems.length && this.sharedItems.length ? 'shared' : tab;
    }

    public clearSelection(): void {
        this.ownItemListComponent.clearSelection();
        this.sharedItemListComponent.clearSelection();
        this.ownSelectedItemId = null;
        this.sharedSelectedItemId = null;
        this.activeTab = 'own';
    }
}
